"use client";

import React from "react";
import { useAgentId } from "@/context/AgentIdContext";
import { fetchBrokerByToken } from "@/api/simpego";
import { Card, CardContent } from "@/components/ui/card";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { X, RefreshCw } from "lucide-react";

const AgentIdDisplay: React.FC = () => {
  const { agentId, agentName, agentToken, isCustomAgentId, setAgentId, setAgentProfile } = useAgentId();
  const { t } = useTranslation();
  const [isRefetching, setIsRefetching] = React.useState(false);

  const handleClearAgentId = () => {
    setAgentId(null);
  };

  const handleRefetchAgent = async () => {
    if (!agentToken) return;
    setIsRefetching(true);
    try {
      const broker = await fetchBrokerByToken(agentToken);
      setAgentProfile({
        agentId: broker.uid || null,
        agentName: broker.name || null,
        agentToken: broker.token || null,
        agentEmails: Array.isArray(broker.emailAddresses) ? broker.emailAddresses : [],
        isDirectAgent: false,
      });
      console.log("AgentIdDisplay - Refetched agent via token", broker);
    } catch (error) {
      console.error("AgentIdDisplay - Failed to refetch agent via token", error);
    } finally {
      setIsRefetching(false);
    }
  };

  if (!agentId || !isCustomAgentId) {
    return null;
  }

  return (
    <Card className="w-full max-w-md mx-auto mb-4 bg-primary text-primary-foreground border-primary test-agent-display">
      <CardContent className="p-3 flex items-center justify-between gap-2 text-sm font-medium">
        <span>
          {t("common.agentIdLabel")}{t("common.colon")} {agentName ? `${agentName} (${agentId})` : agentId}
        </span>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleRefetchAgent}
            disabled={!agentToken || isRefetching}
            className="h-6 w-6 text-primary-foreground hover:bg-primary-foreground/20"
            aria-label={t("common.refreshAgent")}
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleClearAgentId}
            className="h-6 w-6 text-primary-foreground hover:bg-primary-foreground/20"
            aria-label={t("common.clearAgentId")}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default AgentIdDisplay;
